// ============================================
// PHYSICS CONSTANTS & DATABASE
// ============================================

export const C = 299792458; // m/s
export const PROTON_MASS = 0.93827; // GeV/c²
export const ELECTRON_MASS = 0.000511; // GeV/c²
export const MCS_CONSTANT = 13.6; // MeV

// ============================================
// PARTICLE DATABASE
// ============================================
export const PARTICLES = {
    proton: {
        type: 'proton',
        name: 'Proton (p)',
        symbol: 'p',
        mass: 0.93827,
        massUnit: '938.27 MeV/c²',
        lifetime: Infinity,
        lifetimeUnit: 'Stable (>10³⁴ years)',
        charge: 1,
        color: '#3498DB',
        decayMode: 'None (stable)',
        composition: 'uud (2 up + 1 down)',
        description: 'Primary beam particle for MCS tomography. Non-decaying, ideal for scattering measurements.',
        funFact: 'Proton stability is protected by baryon number conservation.'
    },
    pion: {
        type: 'pion',
        name: 'Pion (π⁺)',
        symbol: 'π⁺',
        mass: 0.13957,
        massUnit: '139.57 MeV/c²',
        lifetime: 2.6033e-8,
        lifetimeUnit: '26.0 ns',
        charge: 1,
        color: '#E74C3C',
        decayMode: 'μ⁺ + νμ (99.99%)',
        composition: 'u + d̄ (up + anti-down)',
        description: 'Lightest meson. Can contaminate proton beams.',
        funFact: 'Discovered in 1947 by Cecil Powell using cosmic rays.'
    },
    kaon: {
        type: 'kaon',
        name: 'Kaon (K⁺)',
        symbol: 'K⁺',
        mass: 0.49367,
        massUnit: '493.68 MeV/c²',
        lifetime: 1.2380e-8,
        lifetimeUnit: '12.4 ns',
        charge: 1,
        color: '#9B59B6',
        decayMode: 'μ⁺νμ (63%), π⁺π⁰ (21%)',
        composition: 'u + s̄ (up + anti-strange)',
        description: 'Contains strange quark. Rare in low-momentum beams.',
        funFact: 'K⁰ mixing led to the discovery of CP violation (Nobel 1980).'
    },
    muon: {
        type: 'muon',
        name: 'Muon (μ⁺)',
        symbol: 'μ⁺',
        mass: 0.10566,
        massUnit: '105.66 MeV/c²',
        lifetime: 2.1969e-6,
        lifetimeUnit: '2.20 μs',
        charge: 1,
        color: '#2ECC71',
        decayMode: 'e⁺ + νₑ + ν̄μ (100%)',
        composition: 'Fundamental lepton (no quarks)',
        description: 'Heavy electron. Product of pion decay.',
        funFact: 'Muon g-2 anomaly hints at new physics beyond Standard Model!'
    },
    electron: {
        type: 'electron',
        name: 'Electron (e⁻)',
        symbol: 'e⁻',
        mass: 0.000511,
        massUnit: '0.511 MeV/c²',
        lifetime: Infinity,
        lifetimeUnit: 'Stable',
        charge: -1,
        color: '#00BCD4',
        decayMode: 'None (stable)',
        composition: 'Fundamental lepton',
        description: 'Lightest charged lepton. Minimal MCS due to low mass.',
        funFact: 'First subatomic particle discovered (J.J. Thomson, 1897).'
    },
};

// ============================================
// MATERIAL DATABASE
// ============================================
// X₀ values in cm - critical for MCS calculations
export const MATERIALS = {
    air: {
        type: 'air',
        name: 'Air',
        formula: 'N₂/O₂',
        X0: 30420,
        density: 0.00125,
        zEff: 7.3, // Z in physics engine
        color: '#E8F4FD',
        description: 'Standard atmosphere. Almost no scattering.',
        applications: 'Baseline measurements, drift regions.'
    },
    pvc: {
        type: 'pvc',
        name: 'PVC',
        formula: '(C₂H₃Cl)ₙ',
        X0: 17.0,
        density: 1.30,
        zEff: 17,
        color: '#9B59B6', // Purple in context, Blue in engine (#3B82F6). Keeping context color as it's likely UI based.
        description: 'Polyvinyl Chloride. High scattering due to Chlorine (Z=17).',
        applications: 'Construction, pipes, medical tubing.'
    },
    delrin: {
        type: 'delrin',
        name: 'Delrin',
        formula: '(CH₂O)ₙ',
        X0: 29.3,
        density: 1.41,
        zEff: 7.0,
        color: '#1ABC9C',
        description: 'Polyoxymethylene. Similar density to PVC but much less scattering.',
        applications: 'Precision parts, gears.'
    },
    pmma: {
        type: 'pmma',
        name: 'PMMA',
        formula: '(C₅H₈O₂)ₙ',
        X0: 34.4,
        density: 1.18,
        zEff: 6.5,
        color: '#3498DB',
        description: 'Acrylic/Lucite. Reference material for phantom.',
        applications: 'Medical phantoms, optics.'
    },
    aluminum: {
        type: 'aluminum',
        name: 'Aluminum',
        formula: 'Al',
        X0: 8.9,
        density: 2.70,
        zEff: 13,
        color: '#95A5A6',
        description: 'Light metal. Standard calibration material.',
        applications: 'Structural components, beam windows.'
    },
    water: {
        type: 'water',
        name: 'Water',
        formula: 'H₂O',
        X0: 36.1,
        density: 1.00,
        zEff: 7.4,
        color: '#3498DB',
        description: 'Tissue equivalent. Used for medical physics.',
        applications: 'Dosimetry, phantom calibration.'
    },
    bone: {
        type: 'bone',
        name: 'Bone',
        formula: 'Ca/P',
        X0: 13.8,
        density: 1.85,
        zEff: 13.8,
        color: '#F1C40F',
        description: 'Cortical bone equivalent.',
        applications: 'Medical imaging.'
    },
    graphite: {
        type: 'graphite',
        name: 'Graphite',
        formula: 'C',
        X0: 18.8,
        density: 2.2,
        zEff: 6,
        color: '#2C3E50',
        description: 'Pure carbon target.',
        applications: 'Neutrino targets, beam dumps.'
    },
    hydrogen: {
        type: 'hydrogen',
        name: 'Liquid Hydrogen',
        formula: 'H₂',
        X0: 866,
        density: 0.07,
        zEff: 1,
        color: '#ECF0F1',
        description: 'Pure proton target.',
        applications: 'Scattering experiments.'
    },
    // Extended materials from Physics Engine
    mylar: {
        type: 'mylar',
        name: 'Mylar',
        formula: '(C₁₀H₈O₄)n',
        X0: 28.7,
        density: 1.4,
        zEff: 6.6,
        color: '#E0E7FF',
        description: 'Polyester film.',
        applications: 'Windows, insulation.'
    },
    lead: {
        type: 'lead',
        name: 'Lead',
        formula: 'Pb',
        X0: 0.56,
        density: 11.35,
        zEff: 82,
        color: '#4B5563',
        description: 'Heavy metal, excellent shielding.',
        applications: 'Shielding, collimators.'
    },
    silicon: {
        type: 'silicon',
        name: 'Silicon',
        formula: 'Si',
        X0: 9.37,
        density: 2.33,
        zEff: 14,
        color: '#1F2937',
        description: 'Semiconductor material.',
        applications: 'Detectors, electronics.'
    },
    argon: {
        type: 'argon',
        name: 'Argon Gas',
        formula: 'Ar',
        X0: 10971,
        density: 0.00178,
        zEff: 18,
        color: '#A5F3FC',
        description: 'Noble gas.',
        applications: 'TPCs, drift chambers.'
    },
    iron: {
        type: 'iron',
        name: 'Iron',
        formula: 'Fe',
        X0: 1.76,
        density: 7.87,
        zEff: 26,
        color: '#9CA3AF',
        description: 'Ferromagnetic metal.',
        applications: 'Magnets, structural.'
    },
    aerogel: {
        type: 'aerogel',
        name: 'Aerogel',
        formula: 'SiO₂',
        X0: 100,
        density: 0.2,
        zEff: 11,
        color: '#DBEAFE',
        description: 'Low density material.',
        applications: 'Cherenkov radiators.'
    },
    lyso: {
        type: 'lyso',
        name: 'LYSO Crystal',
        formula: 'Lu₁.₈Y₀.₂SiO₅:Ce',
        X0: 1.14,
        density: 7.1,
        zEff: 60,
        color: '#DDD6FE',
        description: 'Scintillator crystal.',
        applications: 'PET scanners, calorimetry.'
    },
    gold: {
        type: 'gold',
        name: 'Gold',
        formula: 'Au',
        X0: 0.33,
        density: 19.3,
        zEff: 79,
        color: '#FCD34D',
        description: 'Dense noble metal.',
        applications: 'Targets, contacts.'
    },
    gas: {
        type: 'gas',
        name: 'Generic Gas',
        formula: '-',
        X0: 10000,
        density: 0.001,
        zEff: 10,
        color: '#F0FDFA',
        description: 'Generic gas placeholder.',
        applications: 'Simulation defaults.'
    }
};
